import React from 'react';
import { Select } from 'antd';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../utils/languageUtils';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

const GET_ALL_WAREHOUSES = gql`
  query GetWarehouses {
    warehouses: getWarehouseListForHeadquarters {
      id
      name
      siteType
      sortingCode
      parentSiteId
    }
  }
`;

interface WarehouseType {
  id: string;
  name: string;
  siteType: number;
  sortingCode?: string;
  parentSiteId: string;
}

interface WarehousesResponse {
  warehouses: WarehouseType[];
}

interface WarehouseSelectFilterProps {
  label: string;
  value?: string; // 现在存储的是 warehouseId（字符串形式）
  onChange: (value: string | undefined) => void; // 返回 warehouseId
  onSelectSortingName?: (sortingName: string | undefined) => void; // 新增：选择时返回对应的Sorting-name
  onSelectSecondSegment?: (secondSegment: string | undefined) => void; // 新增：选择时返回对应的第二段码
  spanClassName?: string;
  placeholder?: string;
  className?: string;
  hubId?: string; // 可选：根据HUB ID过滤warehouse（基于 parent_site_id）
  firstSortSegment?: string; // 新增：根据1st sort segment过滤（已废弃，保留兼容性）
  disabled?: boolean; // 是否禁用
}

/**
 * Warehouse选择过滤器组件
 * 用于2nd Sort Code的下拉选择，从Warehouse列表中选择
 * 获取site_type=2（WAREHOUSE类型）的所有仓库
 */
export const WarehouseSelectFilter: React.FC<WarehouseSelectFilterProps> = ({
  label,
  value,
  onChange,
  onSelectSortingName,
  onSelectSecondSegment,
  spanClassName,
  placeholder = 'Select Warehouse',
  className,
  hubId,
  firstSortSegment,
  disabled = false
}) => {
  const { data, loading } = useQuery<WarehousesResponse>(GET_ALL_WAREHOUSES, {
    fetchPolicy: 'cache-first', // 优先使用缓存，减少不必要的重新查询
    notifyOnNetworkStatusChange: false, // 减少不必要的重新渲染
  });

  // 构建下拉选项
  const warehouseOptions = React.useMemo(() => {
    if (!data?.warehouses) return [];

    const filteredWarehouses = data.warehouses.filter(warehouse => {
      // 只保留warehouse类型（site_type=2）
      if (warehouse.siteType !== 2) return false;
      // 如果指定了 hubId，则只显示该 Hub 下的 Warehouse（基于 parent_site_id）
      if (hubId) {
        return warehouse.parentSiteId?.toString() === hubId;
      }
      
      // 如果没有指定 hubId，显示所有 Warehouse
      return !!warehouse.sortingCode;
    });

    // 如果 value 存在但不在过滤后的列表中，需要将其添加进去（用于从 URL 恢复时显示）
    if (value) {
      const stringValue = String(value);
      const valueWarehouse = data.warehouses.find(w => String(w.id) === stringValue && w.siteType === 2);
      if (valueWarehouse && !filteredWarehouses.some(w => String(w.id) === stringValue)) {
        filteredWarehouses.push(valueWarehouse);
      }
    }

    const options = filteredWarehouses
      .map(warehouse => {
        // 提取二段码（sorting code的前两段组合，如 "LA-DENCSP"）
        const segments = warehouse.sortingCode?.split('-') || [];
        const secondSegment = segments.length >= 2 ? segments[1] : warehouse.sortingCode;
        return {
          label: `${warehouse.name} (${secondSegment})`,
          value: warehouse.id, // 使用 warehouse.id 作为唯一 value
          secondSegment: secondSegment, // 保存第二段码，供父组件使用
          warehouseId: warehouse.id,
          sortingName: warehouse.name,
          // 用于搜索的文本，包含名称和代码
          searchText: `${warehouse.name} ${secondSegment} ${warehouse.sortingCode}`,
          // 添加原始数据用于调试
          originalData: warehouse
        };
      })
      .filter(opt => opt.value); // 过滤空值

    // 按名称排序，确保一致的显示顺序（ID 唯一）
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [data?.warehouses, hubId, value]); // 添加 value 作为依赖，确保当 value 变化时重新计算

  // value 现在直接是 warehouseId，不需要转换
  // warehouseOptions 已经确保了包含 value 对应的 warehouse（如果存在），所以直接返回 value
  const selectValue = React.useMemo(() => {
    if (!value) return null;
    // 直接返回 value，warehouseOptions 已经确保包含该 warehouse（如果存在）
    return String(value);
  }, [value]);
  
  // 使用统一的样式 Hook，根据语言自动返回合适的样式类名
  const containerClassName = useFilterContainerClassName(className);
  const labelClassName = useFilterLabelClassName(spanClassName);
  const inputContainerClassName = useFilterInputContainerClassName();
  
  return (
    <div className={containerClassName}>
      <span className={labelClassName}>{label}</span>
      <div className={inputContainerClassName}>
        <Select
          showSearch
          allowClear
          placeholder={placeholder}
          value={selectValue === '' || selectValue === null || selectValue === undefined ? null : selectValue}
          disabled={disabled}
          onChange={(val, option) => {
            // val 是 warehouse.id，直接返回给父组件
            const warehouseId = (val as string) || undefined;
            onChange(warehouseId);
            if (onSelectSortingName) {
              const sortingName = option ? ((option as any)?.sortingName as string | undefined) : undefined;
              onSelectSortingName(warehouseId ? sortingName : undefined);
            }
            if (onSelectSecondSegment) {
              // 只在选择时调用 onSelectSecondSegment，清空时由 handleWarehouseIdChange 处理
              // 这样可以避免两个状态更新冲突
              if (warehouseId) {
                const secondSegment = option ? ((option as any)?.secondSegment as string | undefined) : undefined;
                onSelectSecondSegment(secondSegment);
              }
              // 清空时不调用 onSelectSecondSegment，让 handleWarehouseIdChange 统一处理清空逻辑
            }
          }}
          loading={loading}
          options={warehouseOptions}
          filterOption={(input, option) => {
            const searchText = (option as any)?.searchText || '';
            return searchText.toLowerCase().includes(input.toLowerCase());
          }}
          className="w-full"
          size="middle"
        />
      </div>
    </div>
  );
};

